import React, { useCallback, useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { remoteObsAPI, patientAPI } from '../../services/api';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const LOINC_OPTIONS = [
  { type: 'BLOOD_PRESSURE_SYS', code: '8480-6', label: 'Systolic BP', unit: 'mm[Hg]', color: '#ef4444' },
  { type: 'BLOOD_PRESSURE_DIA', code: '8462-4', label: 'Diastolic BP', unit: 'mm[Hg]', color: '#f87171' },
  { type: 'HEART_RATE',         code: '8867-4', label: 'Heart Rate',   unit: '/min',    color: '#94335a' },
  { type: 'OXYGEN_SATURATION',  code: '2708-6', label: 'SpO₂',        unit: '%',       color: '#8b5cf6' },
  { type: 'BODY_TEMPERATURE',   code: '8310-5', label: 'Temperature',  unit: 'Cel',     color: '#f59e0b' },
  { type: 'BLOOD_GLUCOSE',      code: '15074-8', label: 'Glucose',    unit: 'mmol/L',  color: '#10b981' },
  { type: 'WEIGHT',             code: '29463-7', label: 'Weight',     unit: 'kg',      color: '#6366f1' },
  { type: 'RESPIRATORY_RATE',   code: '9279-1',  label: 'Resp. Rate', unit: '/min',    color: '#14b8a6' },
];

const DEF_FORM = {
  patient_id: '', observation_type: 'HEART_RATE',
  value_quantity: '', device_type: '', device_identifier: '',
  is_flagged: false, flag_reason: '',
};

export default function RemoteMonitoring() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isPatientOnly = roles.includes('PATIENT') && !roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE', 'BILLING'].includes(r));
  const [observations, setObservations] = useState([]);
  const [patients, setPatients]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [patientId, setPatientId]       = useState('');
  const [selectedType, setSelectedType] = useState(LOINC_OPTIONS[2]);

  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [delItem, setDelItem]       = useState(null);
  const [form, setForm]             = useState(DEF_FORM);
  const [editForm, setEditForm]     = useState({ value_quantity: '', is_flagged: false, flag_reason: '', device_type: '', device_identifier: '' });
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  const loadObs = useCallback(() => {
    setLoading(true);
    const params = { limit: 200 };
    if (patientId) params.patient_id = patientId;
    remoteObsAPI.list(params)
      .then(r => setObservations(r.data || []))
      .catch(() => setObservations([]))
      .finally(() => setLoading(false));
  }, [patientId]);

  useEffect(() => { loadObs(); }, [loadObs]);
  useEffect(() => {
    if (isPatientOnly) return; // patient list is staff-only; patient data is self-scoped
    patientAPI.list({ limit: 200 })
      .then(r => setPatients(r.data || []))
      .catch(() => {});
  }, [isPatientOnly]);

  const chartData = observations
    .filter(o => o.loinc_code === selectedType.code)
    .sort((a, b) => new Date(a.observed_at) - new Date(b.observed_at))
    .slice(-50)
    .map(o => ({
      time: o.observed_at ? new Date(o.observed_at).toLocaleTimeString() : '—',
      value: Number(o.value_numeric),
    }));

  // ── Vitals physiological range validation ────────────────────────────────────
  const VITALS_RANGES = {
    HEART_RATE:          { min: 20,  max: 300,  label: 'Heart rate',          unit: '/min' },
    BLOOD_PRESSURE_SYS:  { min: 50,  max: 300,  label: 'Systolic BP',         unit: 'mmHg' },
    BLOOD_PRESSURE_DIA:  { min: 20,  max: 200,  label: 'Diastolic BP',        unit: 'mmHg' },
    BODY_TEMPERATURE:    { min: 25,  max: 45,   label: 'Body temperature',    unit: '°C'   },
    OXYGEN_SATURATION:   { min: 50,  max: 100,  label: 'O₂ saturation',       unit: '%'    },
    BLOOD_GLUCOSE:       { min: 0.5, max: 55,   label: 'Blood glucose',       unit: 'mmol/L' },
    WEIGHT:              { min: 0.5, max: 500,  label: 'Body weight',         unit: 'kg'   },
    RESPIRATORY_RATE:    { min: 4,   max: 70,   label: 'Respiratory rate',    unit: '/min' },
    BMI:                 { min: 5,   max: 100,  label: 'BMI',                 unit: 'kg/m²'},
  };
  const validateVitalsRange = (type, value) => {
    const r = VITALS_RANGES[type?.toUpperCase()];
    if (!r) return null;
    const v = Number(value);
    if (isNaN(v)) return `${r.label} must be a number.`;
    if (v < r.min || v > r.max) return `${r.label} ${v} ${r.unit} is outside the physiological range (${r.min}–${r.max} ${r.unit}).`;
    return null;
  };

  // ── Create ────────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    const targetPatientId = isPatientOnly ? user.party_id : form.patient_id;
    if (!targetPatientId || !form.value_quantity) { setError('Patient and value are required'); return; }
    const rangeErr = validateVitalsRange(form.observation_type, form.value_quantity);
    if (rangeErr) { setError(rangeErr); return; }
    setSaving(true); setError('');
    try {
      await remoteObsAPI.create({
        patient_id: targetPatientId,
        observation_type: form.observation_type,
        value_quantity: Number(form.value_quantity),
        device_type: form.device_type || undefined,
        device_identifier: form.device_identifier || undefined,
        is_flagged: Boolean(form.is_flagged),
        flag_reason: form.flag_reason || undefined,
      });
      setShowCreate(false); setForm(DEF_FORM); loadObs();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to record observation');
    } finally { setSaving(false); }
  };

  // ── Update ────────────────────────────────────────────────────────────────────
  const openEdit = (o) => {
    setEditItem(o);
    setEditForm({
      value_quantity: o.value_numeric ?? '',
      is_flagged: o.is_critical || false,
      flag_reason: o.flag_reason || '',
      device_type: o.device_type || '',
      device_identifier: o.device_id || '',
    });
    setError('');
  };

  const handleUpdate = async () => {
    if (!editForm.value_quantity && editForm.value_quantity !== 0) { setError('Value is required'); return; }
    setSaving(true); setError('');
    try {
      await remoteObsAPI.update(editItem.observation_id, {
        value_quantity: Number(editForm.value_quantity),
        is_flagged: Boolean(editForm.is_flagged),
        flag_reason: editForm.flag_reason || undefined,
        device_type: editForm.device_type || undefined,
        device_identifier: editForm.device_identifier || undefined,
      });
      setEditItem(null); loadObs();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update observation');
    } finally { setSaving(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setSaving(true);
    try {
      await remoteObsAPI.delete(delItem.observation_id);
      setDelItem(null); loadObs();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete');
    } finally { setSaving(false); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>Remote Patient Monitoring</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>
            IEEE 11073 · LOINC-coded vitals · IoT device data · Real-time patient observations
          </p>
        </div>
        <button onClick={() => { setShowCreate(true); setError(''); setForm(DEF_FORM); }} style={{
          padding: '9px 18px', background: '#94335a', color: '#fff',
          border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          + Record Observation
        </button>
      </div>

      {/* Standards chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['IEEE 11073 PHD', 'HL7 FHIR Observation', 'LOINC Codes', 'Bluetooth HDP', 'Kenya DPA 2019'].map(s => (
          <span key={s} style={{ padding: '4px 12px', background: '#fdeef5', border: '1px solid #f0c8da', borderRadius: 20, fontSize: 11, fontWeight: 600, color: '#0369a1' }}>
            ✓ {s}
          </span>
        ))}
      </div>

      {/* Filter + Refresh */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {!isPatientOnly && (
        <select value={patientId} onChange={e => setPatientId(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: '9px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
          <option value="">All Patients</option>
          {patients.map(p => <option key={p.party_id} value={p.party_id}>{p.name}</option>)}
        </select>
        )}
        <button onClick={loadObs} style={{ padding: '9px 18px', background: '#f1f5f9', color: '#5e4a53', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Trend Chart */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#2b1b22' }}>Trend Chart</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8b7681' }}>Last 50 readings · {selectedType.label}</p>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {LOINC_OPTIONS.slice(0, 5).map(t => (
              <button key={t.code} onClick={() => setSelectedType(t)} style={{
                padding: '5px 12px', borderRadius: 20, border: '1px solid',
                borderColor: selectedType.code === t.code ? t.color : '#ece3e7',
                background: selectedType.code === t.code ? t.color : '#fff',
                color: selectedType.code === t.code ? '#fff' : '#8b7681',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
              }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="time" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip formatter={(v) => [`${v} ${selectedType.unit}`, selectedType.label]} />
              <Line type="monotone" dataKey="value" stroke={selectedType.color} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a99aa0', fontSize: 13 }}>
            No {selectedType.label} readings available.
          </div>
        )}
      </div>

      {/* Observations Table */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700 }}>
          Latest Readings <span style={{ fontSize: 12, color: '#a99aa0', fontWeight: 400 }}>({observations.length} records)</span>
        </h3>
        {loading ? (
          <div style={{ color: '#a99aa0', fontSize: 13 }}>Loading observations…</div>
        ) : observations.length === 0 ? (
          <div style={{ color: '#a99aa0', textAlign: 'center', padding: '30px 0', fontSize: 13 }}>
            No remote observations yet. Use the button above to record readings.
          </div>
        ) : (
          <div className="hhms-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Patient', 'Measurement', 'LOINC', 'Value', 'Unit', 'Device', 'Observed At', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#8b7681', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '2px solid #f1f5f9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {observations.slice(0, 100).map(o => (
                  <tr key={o.observation_id} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '8px 12px', fontSize: 12, color: '#8b7681' }}>{isPatientOnly ? (user?.name || 'You') : (o.patient_name || `${o.patient_id?.slice(0, 10)}…`)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: '#2b1b22' }}>{(o.observation_type || '—').replace(/_/g, ' ')}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#8b5cf6', fontSize: 11 }}>{o.loinc_code || '—'}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: '#2b1b22' }}>{o.value_numeric ?? o.value_text ?? '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#8b7681' }}>{o.unit || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#8b7681', fontSize: 11 }}>{o.device_type || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#8b7681', whiteSpace: 'nowrap' }}>
                      {o.observed_at ? new Date(o.observed_at).toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                        background: o.is_critical ? '#fee2e2' : '#d1fae5',
                        color: o.is_critical ? '#b91c1c' : '#065f46',
                      }}>
                        {o.is_critical ? '⚠ Critical' : 'Normal'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {isPatientOnly ? <span style={{ color: '#a99aa0' }}>—</span> : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(o)} style={{
                          padding: '4px 10px', background: '#fdeef5', color: '#0369a1',
                          border: '1px solid #f0c8da', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                        }}>Edit</button>
                        <button onClick={() => setDelItem(o)} style={{
                          padding: '4px 10px', background: '#fff1f2', color: '#be123c',
                          border: '1px solid #fecdd3', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                        }}>Delete</button>
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Create Modal ─────────────────────────────────────────────────────── */}
      {showCreate && (
        <Modal title="Record Observation" onClose={() => setShowCreate(false)} fullscreen>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>{isPatientOnly ? 'Recording for' : 'Patient *'}</label>
            {isPatientOnly ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 16, fontSize: 13, fontWeight: 600, color: '#2b1b22' }}>{user?.name} (you)</span>
            ) : (
            <select value={form.patient_id} onChange={e => f('patient_id', e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
              <option value="">Select patient…</option>
              {patients.map(p => <option key={p.party_id} value={p.party_id}>{p.name}</option>)}
            </select>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Observation Type *</label>
              <select value={form.observation_type} onChange={e => f('observation_type', e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 }}>
                {LOINC_OPTIONS.map(o => (
                  <option key={o.type} value={o.type}>{o.label} ({o.unit})</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Value *</label>
              <input type="number" step="0.1" value={form.value_quantity} onChange={e => f('value_quantity', e.target.value)}
                placeholder={`e.g. ${LOINC_OPTIONS.find(o => o.type === form.observation_type)?.unit || ''}`}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Device Type</label>
              <input value={form.device_type} onChange={e => f('device_type', e.target.value)} placeholder="e.g. pulse_oximeter"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Device ID</label>
              <input value={form.device_identifier} onChange={e => f('device_identifier', e.target.value)} placeholder="e.g. BT-001"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="flagged" checked={form.is_flagged} onChange={e => f('is_flagged', e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="flagged" style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600, cursor: 'pointer' }}>Mark as Critical / Flagged</label>
          </div>
          {form.is_flagged && (
            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Flag Reason</label>
              <input value={form.flag_reason} onChange={e => f('flag_reason', e.target.value)} placeholder="Describe the concern…"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setShowCreate(false)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#5e4a53' }}>Cancel</button>
            <button onClick={handleCreate} disabled={saving} style={{ flex: 2, padding: 10, background: saving ? '#a99aa0' : '#94335a', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
              {saving ? 'Saving…' : 'Record Observation'}
            </button>
          </div>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ───────────────────────────────────────────────────────── */}
      {editItem && (
        <Modal title={`Correct — ${(editItem.observation_type || '').replace(/_/g, ' ')}`} onClose={() => setEditItem(null)} width={420}>
          {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}

          <div style={{ marginBottom: 6, padding: '8px 12px', background: '#fef9c3', borderRadius: 8, fontSize: 12, color: '#713f12' }}>
            ℹ️ LOINC code and patient cannot be changed. Only the recorded value, device, and flag can be corrected.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
            {[
              ['Corrected Value *', 'value_quantity', 'number'],
              ['Device Type', 'device_type', 'text'],
              ['Device ID', 'device_identifier', 'text'],
            ].map(([label, key, type]) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>{label}</label>
                <input
                  type={type} step="0.01"
                  value={editForm[key] ?? ''}
                  onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="eflagged" checked={editForm.is_flagged}
              onChange={e => setEditForm(p => ({ ...p, is_flagged: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="eflagged" style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600, cursor: 'pointer' }}>Mark as Critical / Flagged</label>
          </div>
          {editForm.is_flagged && (
            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 4 }}>Flag Reason</label>
              <input value={editForm.flag_reason} onChange={e => setEditForm(p => ({ ...p, flag_reason: e.target.value }))}
                placeholder="Describe the concern…"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={() => setEditItem(null)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#5e4a53' }}>Cancel</button>
            <button onClick={handleUpdate} disabled={saving} style={{ flex: 2, padding: 10, background: saving ? '#a99aa0' : '#94335a', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff' }}>
              {saving ? 'Saving…' : 'Save Correction'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirm ────────────────────────────────────────────────────── */}
      {delItem && (
        <ConfirmDialog
          message={`Delete this ${(delItem.observation_type || '').replace(/_/g, ' ')} reading?`}
          detail="This observation will be permanently removed from the database."
          confirmLabel="Delete"
          loading={saving}
          onConfirm={handleDelete}
          onCancel={() => setDelItem(null)}
        />
      )}
    </div>
  );
}
